/// <reference path="../../types.d.ts" />
import { state } from "../core/state";
import {
  normalizeText,
  escapeHtml,
  highlightMatch,
  formatLifeYears,
  renderAvatarSilhouette,
  searchPersons,
} from "../core/helpers";

export interface ArchiveContext {
  apiRequest: (path: string, method?: string, body?: any) => Promise<any>;
  showSyncToast: (msg: string) => void;
  focusPersonInTree: (pid: string) => void;
  openModal: (title: string, htmlContent: string, isWide?: boolean) => void;
  closeModal: () => void;
  requireAdminThen?: (callback: () => void) => void;
}

let ctx: ArchiveContext = {
  apiRequest: async () => ({ ok: false }),
  showSyncToast: () => {},
  focusPersonInTree: () => {},
  openModal: () => {},
  closeModal: () => {},
  requireAdminThen: (cb) => cb(),
};

export function setupArchiveView(context: ArchiveContext): void {
  ctx = context;
}

export async function loadArchiveManifest(forceReload = false): Promise<any[]> {
  if (!forceReload && state.archiveData && state.archiveData.length > 0) return state.archiveData;
  try {
    const res = await fetch("./archive-manifest.json");
    if (!res.ok) throw new Error("Manifest could not be loaded");
    const staticDocs = await res.json();

    let d1Docs: any[] = [];
    try {
      const d1Res: any = await ctx.apiRequest("archive-docs", "GET");
      const docsList = d1Res && d1Res.ok ? (d1Res.documents || d1Res.data?.documents || []) : [];
      if (Array.isArray(docsList)) {
        d1Docs = docsList.map((d: any) => ({
          ...d,
          person_tags: typeof d.person_tags === "string" ? JSON.parse(d.person_tags || "[]") : (d.person_tags || [])
        }));
      }
    } catch (e) {
      // offline or fallback
    }

    state.archiveData = [...d1Docs, ...staticDocs];
    return state.archiveData;
  } catch (err) {
    console.error("Archive manifest load error:", err);
    state.archiveData = [];
    return [];
  }
}

export function isArchiveFilterActive(): boolean {
  const f = state.archiveFilters || {};
  if (f.showAll) return true;
  if (f.query && f.query.trim().length > 0) return true;
  if (f.contributor && f.contributor !== "" && f.contributor !== "all") return true;
  if (f.cat && f.cat !== "" && f.cat !== "all") return true;
  if (f.format && f.format !== "all") return true;
  if (f.type && f.type !== "all") return true;
  if (f.year && f.year !== "all") return true;
  return false;
}

export function renderArchivePrompt(data: any[]): void {
  const grid = document.getElementById("archive-grid-view");
  const timeline = document.getElementById("archive-timeline-view");
  const countEl = document.getElementById("arch-results-count");
  const resetBtn = document.getElementById("arch-reset-filters-btn");
  const clearPersonBtn = document.getElementById("arch-clear-person-btn");

  if (clearPersonBtn) clearPersonBtn.classList.add("hidden");
  if (resetBtn) resetBtn.classList.add("hidden");

  if (countEl) {
    countEl.textContent = `Belgeleri listelemek için lütfen yukarıdan bir yazışma sahibi veya filtre seçiniz (Toplam ${data.length} belge)`;
  }

  const promptHtml = `
    <div class="archive-prompt-container">
      <div class="archive-prompt-card">
        <div class="archive-prompt-icon-wrap">
          <span class="prompt-icon">📁</span>
          <span class="prompt-subicon">🔍</span>
        </div>
        <h3 class="archive-prompt-title">Belgeleri İncelemek İçin Seçim Yapın</h3>
        <p class="archive-prompt-desc">
          Niksiç ve Hociç aile arşivimizde 20 yıllık resmi devlet yazışmaları, Osmanlı ve Cumhuriyet nüfus kütükleri, CİMER başvuruları ve tarihi belgelerden oluşan <strong>${data.length} adet kayıt</strong> bulunmaktadır.
        </p>
        <p class="archive-prompt-guide">
          👆 Yukarıdaki <strong>"Yazışma Sahibi"</strong> seçim kutusundan bir isim seçerek, kategori butonlarına dokunarak veya arama kutusunu kullanarak belgeleri anında listeleyebilirsiniz.
        </p>
        
        <div class="archive-prompt-shortcuts">
          <span class="shortcuts-title">⚡ Hızlı Seçenekler:</span>
          <button type="button" class="prompt-pill-btn" data-action="pick-person" data-val="I26">
            👤 Turgay Ener (${data.length} Belge)
          </button>
          <button type="button" class="prompt-pill-btn" data-action="pick-cat" data-val="cimer">
            📬 CİMER Başvuruları (64)
          </button>
          <button type="button" class="prompt-pill-btn" data-action="pick-cat" data-val="nufus">
            📜 Nüfus &amp; Vatandaşlık (26)
          </button>
          <button type="button" class="prompt-pill-btn" data-action="pick-cat" data-val="arsiv">
            🏛️ Devlet Arşivleri (14)
          </button>
          <button type="button" class="prompt-pill-btn prompt-gold" data-action="show-all">
            🌟 Tüm ${data.length} Belgeyi Listele
          </button>
        </div>
      </div>
    </div>
  `;

  if (grid) grid.innerHTML = promptHtml;
  if (timeline) timeline.innerHTML = promptHtml;
}

export function resetArchiveFilters(): void {
  state.archiveFilters = { cat: "", format: "all", type: "all", year: "all", contributor: "", query: "", showAll: false };

  const searchInput = document.getElementById("archive-search-input") as HTMLInputElement | null;
  if (searchInput) searchInput.value = "";
  const clearSearchBtn = document.getElementById("archive-clear-search-btn");
  if (clearSearchBtn) clearSearchBtn.classList.add("hidden");

  const contribSelect = document.getElementById("arch-filter-contributor") as HTMLSelectElement | null;
  if (contribSelect) contribSelect.value = "";
  const clearPersonBtn = document.getElementById("arch-clear-person-btn");
  if (clearPersonBtn) clearPersonBtn.classList.add("hidden");

  const formatSelect = document.getElementById("arch-filter-format") as HTMLSelectElement | null;
  if (formatSelect) formatSelect.value = "all";
  const typeSelect = document.getElementById("arch-filter-type") as HTMLSelectElement | null;
  if (typeSelect) typeSelect.value = "all";
  const yearSelect = document.getElementById("arch-filter-year") as HTMLSelectElement | null;
  if (yearSelect) yearSelect.value = "all";

  const chipsContainer = document.getElementById("archive-category-chips");
  if (chipsContainer) {
    chipsContainer.querySelectorAll(".arch-chip").forEach((c) => c.classList.remove("active"));
  }

  renderArchiveView();
}

export function initArchiveView(): void {
  const searchInput = document.getElementById("archive-search-input") as HTMLInputElement | null;
  const clearBtn = document.getElementById("archive-clear-search-btn");
  if (searchInput) {
    searchInput.addEventListener("input", (e: any) => {
      state.archiveFilters.query = e.target.value.trim();
      if (clearBtn) clearBtn.classList.toggle("hidden", !state.archiveFilters.query);
      renderArchiveView();
    });
  }
  if (clearBtn) {
    clearBtn.addEventListener("click", () => {
      if (searchInput) searchInput.value = "";
      state.archiveFilters.query = "";
      clearBtn.classList.add("hidden");
      renderArchiveView();
    });
  }

  // View mode toggles: Grid vs Timeline
  const modeGridBtn = document.getElementById("arch-mode-grid");
  const modeTimelineBtn = document.getElementById("arch-mode-timeline");
  if (modeGridBtn && modeTimelineBtn) {
    modeGridBtn.addEventListener("click", () => {
      state.archiveViewMode = "grid";
      modeGridBtn.classList.add("active");
      modeTimelineBtn.classList.remove("active");
      const gv = document.getElementById("archive-grid-view");
      const tv = document.getElementById("archive-timeline-view");
      if (gv) gv.classList.remove("hidden");
      if (tv) tv.classList.add("hidden");
      renderArchiveView();
    });
    modeTimelineBtn.addEventListener("click", () => {
      state.archiveViewMode = "timeline";
      modeTimelineBtn.classList.add("active");
      modeGridBtn.classList.remove("active");
      const gv = document.getElementById("archive-grid-view");
      const tv = document.getElementById("archive-timeline-view");
      if (gv) gv.classList.add("hidden");
      if (tv) tv.classList.remove("hidden");
      renderArchiveView();
    });
  }

  // Featured Prominent Person filter dropdown
  const contribFilterSelect = document.getElementById("arch-filter-contributor") as HTMLSelectElement | null;
  const clearPersonBtn = document.getElementById("arch-clear-person-btn");
  if (contribFilterSelect) {
    contribFilterSelect.addEventListener("change", (e: any) => {
      const val = e.target.value;
      if (val === "all") {
        state.archiveFilters.contributor = "all";
        state.archiveFilters.showAll = true;
      } else {
        state.archiveFilters.contributor = val;
        state.archiveFilters.showAll = false;
      }
      if (clearPersonBtn) clearPersonBtn.classList.toggle("hidden", !val);
      renderArchiveView();
    });
  }
  if (clearPersonBtn) {
    clearPersonBtn.addEventListener("click", () => {
      state.archiveFilters.contributor = "";
      if (contribFilterSelect) contribFilterSelect.value = "";
      clearPersonBtn.classList.add("hidden");
      renderArchiveView();
    });
  }

  // Category chips
  const chipsContainer = document.getElementById("archive-category-chips");
  if (chipsContainer) {
    chipsContainer.querySelectorAll(".arch-chip").forEach((chip: any) => {
      chip.addEventListener("click", () => {
        const catVal = chip.dataset.cat || "all";
        const isAlreadyActive = chip.classList.contains("active");

        chipsContainer.querySelectorAll(".arch-chip").forEach((c) => c.classList.remove("active"));

        if (catVal === "all") {
          chip.classList.add("active");
          state.archiveFilters.showAll = true;
          state.archiveFilters.cat = "";
        } else if (isAlreadyActive) {
          state.archiveFilters.cat = "";
          state.archiveFilters.showAll = false;
        } else {
          chip.classList.add("active");
          state.archiveFilters.cat = catVal;
          state.archiveFilters.showAll = false;
        }
        renderArchiveView();
      });
    });
  }

  // Secondary filters
  const formatSelect = document.getElementById("arch-filter-format") as HTMLSelectElement | null;
  if (formatSelect) {
    formatSelect.addEventListener("change", (e: any) => {
      state.archiveFilters.format = e.target.value;
      renderArchiveView();
    });
  }

  const typeSelect = document.getElementById("arch-filter-type") as HTMLSelectElement | null;
  if (typeSelect) {
    typeSelect.addEventListener("change", (e: any) => {
      state.archiveFilters.type = e.target.value;
      renderArchiveView();
    });
  }

  const yearSelect = document.getElementById("arch-filter-year") as HTMLSelectElement | null;
  if (yearSelect) {
    yearSelect.addEventListener("change", (e: any) => {
      state.archiveFilters.year = e.target.value;
      renderArchiveView();
    });
  }

  // Reset Filters button
  const resetFiltersBtn = document.getElementById("arch-reset-filters-btn");
  if (resetFiltersBtn) {
    resetFiltersBtn.addEventListener("click", resetArchiveFilters);
  }

  // Shortcut pills click delegation
  document.addEventListener("click", (e) => {
    const target = (e.target as HTMLElement).closest(".prompt-pill-btn") as HTMLElement | null;
    if (!target) return;
    const action = target.dataset.action;
    const val = target.dataset.val;

    if (action === "pick-person" && val) {
      state.archiveFilters.contributor = val;
      state.archiveFilters.showAll = false;
      if (contribFilterSelect) contribFilterSelect.value = val;
      if (clearPersonBtn) clearPersonBtn.classList.remove("hidden");
      renderArchiveView();
    } else if (action === "pick-cat" && val) {
      state.archiveFilters.cat = val;
      state.archiveFilters.showAll = false;
      if (chipsContainer) {
        chipsContainer.querySelectorAll(".arch-chip").forEach((c: any) => {
          c.classList.toggle("active", c.dataset.cat === val);
        });
      }
      renderArchiveView();
    } else if (action === "show-all") {
      state.archiveFilters.showAll = true;
      state.archiveFilters.cat = "";
      state.archiveFilters.contributor = "all";
      if (contribFilterSelect) contribFilterSelect.value = "all";
      if (chipsContainer) {
        chipsContainer.querySelectorAll(".arch-chip").forEach((c: any) => {
          c.classList.toggle("active", c.dataset.cat === "all");
        });
      }
      renderArchiveView();
    }
  });

  // Add Document Button (for admins only!)
  const addDocBtn = document.getElementById("archive-add-doc-btn");
  if (addDocBtn) {
    addDocBtn.style.display = (state.role === "admin") ? "inline-flex" : "none";
    addDocBtn.addEventListener("click", () => {
      if (state.role !== "admin") {
        if (ctx.requireAdminThen) {
          ctx.requireAdminThen(openArchiveUploadModal);
        } else {
          alert("Bu işlem sadece Yönetici (Admin) yetkisi ile gerçekleştirilebilir.");
        }
        return;
      }
      openArchiveUploadModal();
    });
  }

  // Modal controls
  const closeBtn = document.getElementById("arch-view-close-btn");
  const backdrop = document.getElementById("archive-viewer-backdrop");
  if (closeBtn) closeBtn.addEventListener("click", closeArchiveViewer);
  if (backdrop) {
    backdrop.addEventListener("click", (e) => {
      if (e.target === backdrop) closeArchiveViewer();
    });
  }

  const prevBtn = document.getElementById("arch-prev-btn");
  const nextBtn = document.getElementById("arch-next-btn");
  if (prevBtn) prevBtn.addEventListener("click", prevArchiveDocument);
  if (nextBtn) nextBtn.addEventListener("click", nextArchiveDocument);

  // Lightbox tools
  const zoomInBtn = document.getElementById("arch-zoom-in");
  const zoomOutBtn = document.getElementById("arch-zoom-out");
  const zoomResetBtn = document.getElementById("arch-zoom-reset");
  const rotateBtn = document.getElementById("arch-rotate");
  if (zoomInBtn) zoomInBtn.addEventListener("click", () => adjustLightboxZoom(0.25));
  if (zoomOutBtn) zoomOutBtn.addEventListener("click", () => adjustLightboxZoom(-0.25));
  if (zoomResetBtn) zoomResetBtn.addEventListener("click", resetLightboxZoom);
  if (rotateBtn) rotateBtn.addEventListener("click", rotateLightboxImage);

  // Keyboard navigation for modal
  window.addEventListener("keydown", (e) => {
    if (backdrop && !backdrop.classList.contains("hidden")) {
      if (e.key === "Escape") closeArchiveViewer();
      if (e.key === "ArrowLeft") prevArchiveDocument();
      if (e.key === "ArrowRight") nextArchiveDocument();
    }
  });
}

export function openArchiveUploadModal(): void {
  if (state.role !== "admin") {
    if (ctx.requireAdminThen) {
      ctx.requireAdminThen(openArchiveUploadModal);
    } else {
      alert("Bu işlem sadece Yönetici (Admin) yetkisi ile gerçekleştirilebilir.");
    }
    return;
  }

  const modalHtml = `
    <form id="add-archive-doc-form" style="display:flex;flex-direction:column;gap:1rem;">
      <p class="section-subtitle" style="margin-bottom:0.25rem;">
        Aile arşivine yeni bir resmi yazışma, dilekçe, nüfus belgesi veya tarihi fotoğraf ekleyin.
      </p>

      <div class="form-field">
        <label>📄 Dosya Seçin (Görsel veya PDF)*</label>
        <input type="file" id="arch-file-input" accept="image/*,.pdf" class="form-input" required />
      </div>

      <div id="arch-preview-box" style="display:none; text-align:center; padding:0.75rem; background:var(--bg-primary); border-radius:10px; border:1px solid var(--border-subtle);">
        <img id="arch-preview-img" style="max-height:200px; max-width:100%; border-radius:8px; object-fit:contain; display:none;" />
        <div id="arch-preview-file-icon" style="font-size:2.8rem; display:none;">📕</div>
        <div id="arch-preview-size" style="font-size:0.8rem; color:var(--text-muted); margin-top:0.35rem; font-weight:500;"></div>
      </div>

      <div class="form-field">
        <label>🏷️ Belge Başlığı*</label>
        <input type="text" id="arch-title-input" class="form-input" placeholder="Örn: 1968 Saraybosna Nüfus Müdürlüğü Yazışması" required />
      </div>

      <div class="form-field" style="position:relative;">
        <label>👤 Belgeyi Sağlayan / Yazışma Sahibi (Soy Ağacından)*</label>
        <input type="text" id="arch-contributor-search" class="form-input" placeholder="Aile ağacından isim arayın (örn: Turgay, Emrecan, Hasan...)" autocomplete="off" required />
        <input type="hidden" id="arch-contributor-id" value="" />
        <input type="hidden" id="arch-contributor-name" value="" />
        <div id="arch-contributor-selected" style="display:none; margin-top:0.35rem; align-items:center; gap:0.5rem; background:rgba(35,83,64,0.12); border:1px solid rgba(35,83,64,0.3); border-radius:8px; padding:0.35rem 0.65rem;">
          <span style="font-weight:700; color:var(--accent-primary);" id="arch-contributor-selected-name"></span>
          <button type="button" id="arch-contributor-clear-btn" class="mini-btn" style="padding:0 5px; font-size:0.7rem;">✕ Değiştir</button>
        </div>
        <div id="arch-contributor-results" class="autocomplete-list hidden" style="position:absolute; top:calc(100% + 4px); left:0; right:0; z-index:9999; max-height:220px; overflow-y:auto; background:var(--bg-surface); border:1.5px solid var(--border-strong); border-radius:12px; box-shadow:var(--shadow-lg); padding:0.35rem;"></div>
      </div>

      <div style="display:grid; grid-template-columns:1fr 1fr; gap:0.75rem;">
        <div class="form-field">
          <label>🏛️ Kategori*</label>
          <select id="arch-cat-select" class="form-input" required>
            <option value="cimer">🏛️ CİMER Başvuruları</option>
            <option value="bosnia">🇧🇦 Bosna &amp; Saraybosna</option>
            <option value="montenegro">🇲🇪 Karadağ &amp; Nikşiç</option>
            <option value="consulate">🌐 Konsolosluk &amp; Dışişleri</option>
            <option value="civil">📜 Nüfus &amp; Vatandaşlık</option>
            <option value="correspondence">✉️ Diğer Aile Yazışmaları</option>
          </select>
        </div>
        <div class="form-field">
          <label>📂 Belge Türü*</label>
          <select id="arch-type-select" class="form-input" required>
            <option value="Müracaat / Dilekçe">Müracaat / Dilekçe</option>
            <option value="Resmi Cevap / Karar">Resmi Cevap / Karar</option>
            <option value="Arşiv Araştırması">Arşiv Araştırması</option>
            <option value="Nüfus Kayıt Örneği">Nüfus Kayıt Örneği</option>
            <option value="Vatandaşlık Belgesi">Vatandaşlık Belgesi</option>
            <option value="Fotoğraf / Vesika">Fotoğraf / Vesika</option>
            <option value="Diğer">Diğer</option>
          </select>
        </div>
      </div>

      <div style="display:grid; grid-template-columns:1.2fr 0.8fr 1fr; gap:0.75rem;">
        <div class="form-field">
          <label>🏢 Merci / Kurum</label>
          <input type="text" id="arch-inst-input" class="form-input" placeholder="Örn: T.C. Dışişleri Bakanlığı" />
        </div>
        <div class="form-field">
          <label>📅 Yıl*</label>
          <input type="number" id="arch-year-input" class="form-input" min="1800" max="2100" value="${new Date().getFullYear()}" required />
        </div>
        <div class="form-field">
          <label>📆 Tam Tarih</label>
          <input type="date" id="arch-date-input" class="form-input" value="${new Date().toISOString().split("T")[0]}" />
        </div>
      </div>

      <div class="form-field">
        <label>📝 Açıklama / Notlar</label>
        <textarea id="arch-desc-input" class="form-input" rows="2" placeholder="Belgenin içeriği, konusu veya arşivleme notları..."></textarea>
      </div>

      <div id="arch-upload-error" class="auth-error hidden"></div>

      <div class="modal-actions" style="margin-top:0.5rem;">
        <button type="button" class="mini-btn" id="arch-upload-cancel-btn">İptal</button>
        <button type="submit" class="primary-btn small-btn" id="arch-upload-submit-btn">📁 Arşive Kaydet</button>
      </div>
    </form>
  `;

  ctx.openModal("＋ Yeni Arşiv Belgesi Ekle", modalHtml, true);

  let fileDataUrl = "";
  let detectedFormat = "image";
  let detectedExt = ".jpg";
  let origFilename = "";
  let sizeBytes = 0;
  let sizeFormatted = "";

  const fileInput = document.getElementById("arch-file-input") as HTMLInputElement | null;
  const previewBox = document.getElementById("arch-preview-box");
  const previewImg = document.getElementById("arch-preview-img") as HTMLImageElement | null;
  const previewFileIcon = document.getElementById("arch-preview-file-icon");
  const previewSize = document.getElementById("arch-preview-size");
  const errBox = document.getElementById("arch-upload-error");

  fileInput?.addEventListener("change", (e: any) => {
    const file = e.target.files?.[0];
    if (!file) return;

    origFilename = file.name;
    const ext = "." + (file.name.split(".").pop() || "").toLowerCase();
    detectedExt = ext;
    const isImage = file.type.startsWith("image/");
    const isPdf = file.type === "application/pdf" || ext === ".pdf";

    if (!isImage && !isPdf) {
      if (errBox) {
        errBox.textContent = "Lütfen sadece görsel (JPG/PNG/WEBP) veya PDF dosyası seçiniz.";
        errBox.classList.remove("hidden");
      }
      fileInput.value = "";
      return;
    }

    if (file.size > 15 * 1024 * 1024) {
      if (errBox) {
        errBox.textContent = "Dosya boyutu çok büyük (maksimum 15 MB).";
        errBox.classList.remove("hidden");
      }
      fileInput.value = "";
      return;
    }

    if (errBox) errBox.classList.add("hidden");

    if (isImage) {
      detectedFormat = "image";
      const reader = new FileReader();
      reader.onload = (re: any) => {
        const img = new Image();
        img.onload = () => {
          const maxDim = 1600;
          let w = img.width, h = img.height;
          if (w > maxDim || h > maxDim) {
            if (w > h) { h = Math.round((h * maxDim) / w); w = maxDim; }
            else { w = Math.round((w * maxDim) / h); h = maxDim; }
          }
          const c = document.createElement("canvas");
          c.width = w; c.height = h;
          const cx = c.getContext("2d");
          if (cx) {
            cx.drawImage(img, 0, 0, w, h);
            fileDataUrl = c.toDataURL("image/jpeg", 0.84);
            detectedExt = ".jpg";
            sizeBytes = Math.round((fileDataUrl.length * 3) / 4);
            const kb = Math.round(sizeBytes / 1024);
            sizeFormatted = kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;

            if (previewImg) {
              previewImg.src = fileDataUrl;
              previewImg.style.display = "block";
            }
            if (previewFileIcon) previewFileIcon.style.display = "none";
            if (previewSize) previewSize.textContent = `Optimize edildi: ${w}x${h}px • ${sizeFormatted}`;
            if (previewBox) previewBox.style.display = "block";
          }
        };
        img.src = String(re.target.result);
      };
      reader.readAsDataURL(file);
    } else {
      // PDF
      detectedFormat = "pdf";
      detectedExt = ".pdf";
      sizeBytes = file.size;
      const kb = Math.round(sizeBytes / 1024);
      sizeFormatted = kb > 1024 ? `${(kb / 1024).toFixed(1)} MB` : `${kb} KB`;

      const reader = new FileReader();
      reader.onload = (re: any) => {
        fileDataUrl = String(re.target.result);
        if (previewImg) previewImg.style.display = "none";
        if (previewFileIcon) {
          previewFileIcon.style.display = "block";
          previewFileIcon.textContent = "📕";
        }
        if (previewSize) previewSize.textContent = `${file.name} • ${sizeFormatted} (PDF Belgesi)`;
        if (previewBox) previewBox.style.display = "block";
      };
      reader.readAsDataURL(file);
    }
  });

  // Contributor autocomplete setup
  const contribSearch = document.getElementById("arch-contributor-search") as HTMLInputElement;
  const contribId = document.getElementById("arch-contributor-id") as HTMLInputElement;
  const contribName = document.getElementById("arch-contributor-name") as HTMLInputElement;
  const contribSelected = document.getElementById("arch-contributor-selected");
  const contribSelectedName = document.getElementById("arch-contributor-selected-name");
  const contribClearBtn = document.getElementById("arch-contributor-clear-btn");
  const contribResults = document.getElementById("arch-contributor-results");

  contribSearch?.addEventListener("input", () => {
    const rawQ = contribSearch.value.trim();
    const q = normalizeText(rawQ);
    if (!rawQ) {
      contribResults?.classList.add("hidden");
      return;
    }
    const matches = searchPersons(state.personList, rawQ, { limit: 8 });

    if (contribResults) {
      if (matches.length === 0) {
        contribResults.innerHTML = `<div style="padding:0.6rem;font-size:0.8rem;color:var(--text-muted);text-align:center;">Kişi bulunamadı</div>`;
      } else {
        contribResults.innerHTML = matches.map((p) => {
          const isDeceased = Boolean(p.deceased);
          return `
            <div class="search-result-item sex-${p.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" data-contrib-id="${p.id}" data-contrib-name="${escapeHtml(p.name)}" style="padding:0.4rem 0.6rem;cursor:pointer;">
              <span class="sr-avatar sex-${p.sex} ${isDeceased ? 'is-deceased' : 'is-living'}" aria-hidden="true">${renderAvatarSilhouette(p.sex, isDeceased)}</span>
              <div class="sr-main">
                <span class="sr-name">${highlightMatch(p.name, q)}</span>
                <span class="sr-sub">${escapeHtml(formatLifeYears(p))}</span>
              </div>
            </div>
          `;
        }).join("");
      }
      contribResults.classList.remove("hidden");
    }
  });

  contribResults?.addEventListener("click", (e) => {
    const item = (e.target as HTMLElement).closest("[data-contrib-id]") as HTMLElement;
    if (item && item.dataset.contribId && item.dataset.contribName) {
      contribId.value = item.dataset.contribId;
      contribName.value = item.dataset.contribName;
      if (contribSelectedName) contribSelectedName.textContent = `👤 ${item.dataset.contribName}`;
      if (contribSelected) contribSelected.style.display = "flex";
      if (contribSearch) {
        contribSearch.style.display = "none";
        contribSearch.required = false;
      }
      contribResults.classList.add("hidden");
    }
  });

  contribClearBtn?.addEventListener("click", () => {
    contribId.value = "";
    contribName.value = "";
    if (contribSelected) contribSelected.style.display = "none";
    if (contribSearch) {
      contribSearch.style.display = "block";
      contribSearch.value = "";
      contribSearch.required = true;
      contribSearch.focus();
    }
  });

  document.getElementById("arch-upload-cancel-btn")?.addEventListener("click", ctx.closeModal);

  document.getElementById("add-archive-doc-form")?.addEventListener("submit", async (e) => {
    e.preventDefault();
    if (!fileDataUrl) {
      if (errBox) {
        errBox.textContent = "Lütfen bir dosya seçiniz.";
        errBox.classList.remove("hidden");
      }
      return;
    }

    const title = (document.getElementById("arch-title-input") as HTMLInputElement)?.value.trim();
    if (!title) {
      if (errBox) {
        errBox.textContent = "Lütfen belge başlığını giriniz.";
        errBox.classList.remove("hidden");
      }
      return;
    }

    const catSelect = document.getElementById("arch-cat-select") as HTMLSelectElement | null;
    const category = catSelect?.value || "cimer";
    const categoryName = catSelect?.options[catSelect.selectedIndex]?.text?.replace(/^[^\s]+\s+/, "") || "Arşiv";
    const docType = (document.getElementById("arch-type-select") as HTMLSelectElement)?.value || "Diğer";
    const institution = (document.getElementById("arch-inst-input") as HTMLInputElement)?.value.trim() || "Resmi Kurum";
    const year = parseInt((document.getElementById("arch-year-input") as HTMLInputElement)?.value) || new Date().getFullYear();
    const date = (document.getElementById("arch-date-input") as HTMLInputElement)?.value || "";
    const description = (document.getElementById("arch-desc-input") as HTMLTextAreaElement)?.value.trim() || "";

    const contributorId = (document.getElementById("arch-contributor-id") as HTMLInputElement)?.value || "";
    const contributorName = (document.getElementById("arch-contributor-name") as HTMLInputElement)?.value || "";
    if (!contributorId || !contributorName) {
      if (errBox) {
        errBox.textContent = "Lütfen belgeyi sağlayan aile üyesini seçiniz.";
        errBox.classList.remove("hidden");
      }
      return;
    }

    const submitBtn = document.getElementById("arch-upload-submit-btn") as HTMLButtonElement | null;
    if (submitBtn) {
      submitBtn.textContent = "Arşive Yükleniyor...";
      submitBtn.disabled = true;
    }

    try {
      const res = await ctx.apiRequest("archive-docs", "POST", {
        title,
        category,
        categoryName,
        institution,
        docType,
        year,
        date,
        format: detectedFormat,
        extension: detectedExt,
        fileUrl: fileDataUrl,
        originalFilename: origFilename || "belge",
        sizeBytes,
        sizeFormatted,
        description,
        contributorId,
        contributorName,
        personTags: []
      });

      if (res && res.ok) {
        ctx.closeModal();
        ctx.showSyncToast("✅ Belge başarıyla arşive eklendi ve buluta kaydedildi");
        await loadArchiveManifest(true);
        renderArchiveView();
      } else {
        throw new Error(res?.error || "Belge kaydedilemedi");
      }
    } catch (err: any) {
      console.error("Archive doc upload error:", err);
      if (errBox) {
        errBox.textContent = err.message || "Yükleme sırasında hata oluştu";
        errBox.classList.remove("hidden");
      }
      if (submitBtn) {
        submitBtn.textContent = "📁 Arşive Kaydet";
        submitBtn.disabled = false;
      }
    }
  });
}

export async function renderArchiveView(): Promise<void> {
  const data = await loadArchiveManifest();
  if (!data || data.length === 0) return;

  // Populate or refresh contributor select dropdown with counts
  const contribFilterSelect = document.getElementById("arch-filter-contributor") as HTMLSelectElement | null;
  if (contribFilterSelect) {
    const counts: Record<string, { count: number; name: string; id: string }> = {};

    for (const d of data) {
      const cName = d.contributor_name || "Turgay Ener";
      const cId = d.contributor_id || "I26";
      const key = `${cName}__${cId}`;
      if (!counts[key]) counts[key] = { count: 0, name: cName, id: cId };
      counts[key].count++;
    }

    const sortedContributors = Object.values(counts).sort((a, b) => b.count - a.count || a.name.localeCompare(b.name, "tr"));
    const currentVal = state.archiveFilters.contributor || "";

    const optionsHtml = [
      `<option value=""${currentVal === "" ? " selected" : ""}>Yazışma Sahibi Seçin...</option>`,
      `<option value="all"${currentVal === "all" ? " selected" : ""}>Tüm Kişiler / Tüm Belgeler (${data.length})</option>`,
      ...sortedContributors.map((c) => {
        const val = c.id || c.name;
        const sel = currentVal === val ? " selected" : "";
        return `<option value="${val}"${sel}>${escapeHtml(c.name)} (${c.count})</option>`;
      })
    ].join("");

    if (contribFilterSelect.innerHTML !== optionsHtml) {
      contribFilterSelect.innerHTML = optionsHtml;
    }
    if (contribFilterSelect.value !== currentVal) {
      contribFilterSelect.value = currentVal;
    }
  }

  // If no filter is active, show the prompt instead of rendering all 109 cards
  if (!isArchiveFilterActive()) {
    renderArchivePrompt(data);
    return;
  }

  // Filter is active! Show reset button and update clear person button
  const resetBtn = document.getElementById("arch-reset-filters-btn");
  if (resetBtn) resetBtn.classList.remove("hidden");

  const clearPersonBtn = document.getElementById("arch-clear-person-btn");
  if (clearPersonBtn) clearPersonBtn.classList.toggle("hidden", !state.archiveFilters.contributor);

  const q = normalizeText(state.archiveFilters.query || "");
  const cat = state.archiveFilters.cat || "";
  const fmt = state.archiveFilters.format || "all";
  const typ = state.archiveFilters.type || "all";
  const yr = state.archiveFilters.year || "all";
  const contrib = state.archiveFilters.contributor || "";

  // Filter
  const filtered = data.filter((d) => {
    if (cat && cat !== "all" && d.category !== cat) return false;
    if (fmt !== "all" && d.format !== fmt) return false;
    if (typ !== "all" && d.doc_type !== typ) return false;
    if (yr !== "all" && String(d.year) !== yr) return false;
    if (contrib && contrib !== "all") {
      if (contrib.startsWith("tag:")) {
        const tagName = contrib.slice(4).toLowerCase();
        const docTags = (d.person_tags || []).map((t: string) => String(t).toLowerCase());
        const docTitle = String(d.title || "").toLowerCase();
        const docDesc = String(d.description || "").toLowerCase();
        if (!docTags.includes(tagName) && !docTitle.includes(tagName) && !docDesc.includes(tagName)) {
          return false;
        }
      } else {
        const docContribId = d.contributor_id || "I26";
        const docContribName = d.contributor_name || "Turgay Ener";
        if (contrib !== docContribId && contrib !== docContribName) return false;
      }
    }
    if (q) {
      const pTagsStr = (d.person_tags || []).join(" ");
      const hay = normalizeText(`${d.title} ${d.institution} ${d.description} ${d.original_filename} ${d.year} ${d.contributor_name || "Turgay Ener"} ${d.text_content || ""} ${pTagsStr}`);
      if (!hay.includes(q)) return false;
    }
    return true;
  });

  // Update count badge
  const countEl = document.getElementById("arch-results-count");
  if (countEl) {
    countEl.textContent = `${filtered.length} / ${data.length} belge listeleniyor`;
  }

  // Render Grid or Timeline
  if (state.archiveViewMode === "grid") {
    renderArchiveGrid(filtered);
  } else {
    renderArchiveTimeline(filtered);
  }
}

export function renderArchiveGrid(docs: any[]): void {
  const grid = document.getElementById("archive-grid-view") as (HTMLElement & { _hasClickListener?: boolean }) | null;
  if (!grid) return;

  if (docs.length === 0) {
    grid.innerHTML = `
      <div style="grid-column: 1 / -1; text-align: center; padding: 4rem 1rem; color: var(--text-muted);">
        <div style="font-size: 3rem; margin-bottom: 0.5rem;">🔍</div>
        <div style="font-size: 1.1rem; font-weight: 600; color: var(--text-primary); margin-bottom: 0.25rem;">Aramanızla Eşleşen Belge Bulunamadı</div>
        <div style="font-size: 0.85rem;">Filtreleri sıfırlayarak veya farklı bir anahtar kelime arayarak tekrar deneyebilirsiniz.</div>
      </div>
    `;
    return;
  }

  grid.innerHTML = docs.map((d) => {
    const isImg = d.format === "image";
    const isPdf = d.format === "pdf";
    const fmtBadge = (d.extension || "").replace(".", "").toUpperCase();

    let previewContent = "";
    if (isImg) {
      previewContent = `<img src="${d.file_url}" alt="${escapeHtml(d.title)}" loading="lazy" />`;
    } else if (isPdf) {
      previewContent = `
        <div class="arch-card-icon-preview">
          <span>📕</span>
          <span>Resmi PDF Belgesi</span>
        </div>
      `;
    } else {
      previewContent = `
        <div class="arch-card-icon-preview">
          <span>📜</span>
          <span>Dilekçe &amp; Metin</span>
        </div>
      `;
    }

    const contribName = d.contributor_name || "Turgay Ener";
    const contribId = d.contributor_id || "I26";

    return `
      <div class="archive-card" data-doc-id="${d.id}">
        <div class="arch-card-preview">
          ${previewContent}
          <div class="arch-card-top-badges">
            <button type="button" class="arch-contributor-badge" data-person-id="${contribId}" title="Soy Ağacında Konumlandır: ${escapeHtml(contribName)}">👤 ${escapeHtml(contribName)}</button>
            <span class="arch-type-badge">${escapeHtml(d.doc_type)}</span>
          </div>
        </div>
        <div class="arch-card-body">
          <div class="arch-card-inst">${escapeHtml(d.institution)}</div>
          <h4 class="arch-card-title">${escapeHtml(d.title)}</h4>
          <p class="arch-card-desc">${escapeHtml(d.description)}</p>
        </div>
        <div class="arch-card-footer">
          <span>📅 ${d.date ? d.date.split("-").reverse().join(".") : d.year} • ${d.size_formatted}</span>
          <span class="arch-format-badge">${fmtBadge}</span>
        </div>
      </div>
    `;
  }).join("");

  if (!grid._hasClickListener) {
    grid.addEventListener("click", (e) => {
      const contribBtn = (e.target as HTMLElement).closest(".arch-contributor-badge") as HTMLElement;
      if (contribBtn && contribBtn.dataset.personId) {
        e.stopPropagation();
        ctx.focusPersonInTree(contribBtn.dataset.personId);
        return;
      }
      const card = (e.target as HTMLElement).closest(".archive-card") as HTMLElement;
      if (card && card.dataset.docId) openArchiveDocument(card.dataset.docId);
    });
    grid._hasClickListener = true;
  }
}

export function renderArchiveTimeline(docs: any[]): void {
  const container = document.getElementById("archive-timeline-view") as (HTMLElement & { _hasClickListener?: boolean }) | null;
  if (!container) return;

  if (docs.length === 0) {
    container.innerHTML = `<div style="text-align:center; padding:3rem; color:var(--text-muted);">Belge bulunamadı.</div>`;
    return;
  }

  container.innerHTML = docs.map((d) => {
    const icon = d.format === "pdf" ? "📄" : d.format === "image" ? "🖼️" : "📝";
    const dateFormatted = d.date ? d.date.split("-").reverse().join(".") : d.year;

    const contribName = d.contributor_name || "Turgay Ener";
    const contribId = d.contributor_id || "I26";

    return `
      <div class="arch-timeline-item" data-doc-id="${d.id}">
        <div class="arch-timeline-dot">${icon}</div>
        <div class="arch-timeline-card">
          <div class="arch-timeline-left">
            <div class="arch-timeline-date">🗓️ ${dateFormatted}  •  ${escapeHtml(d.institution)}  •  <span class="arch-contributor-badge" data-person-id="${contribId}" style="display:inline-flex;padding:0.1rem 0.4rem;font-size:0.65rem;">👤 ${escapeHtml(contribName)}</span></div>
            <div class="arch-timeline-title">${escapeHtml(d.title)}</div>
            <div class="arch-timeline-desc">${escapeHtml(d.description)}</div>
          </div>
          <div class="arch-timeline-right">
            <span class="arch-type-badge">${escapeHtml(d.doc_type)}</span>
            <span class="arch-format-badge">${(d.extension || "").replace(".", "").toUpperCase()}</span>
          </div>
        </div>
      </div>
    `;
  }).join("");

  if (!container._hasClickListener) {
    container.addEventListener("click", (e) => {
      const contribBtn = (e.target as HTMLElement).closest(".arch-contributor-badge") as HTMLElement;
      if (contribBtn && contribBtn.dataset.personId) {
        e.stopPropagation();
        ctx.focusPersonInTree(contribBtn.dataset.personId);
        return;
      }
      const item = (e.target as HTMLElement).closest(".arch-timeline-item") as HTMLElement;
      if (item && item.dataset.docId) openArchiveDocument(item.dataset.docId);
    });
    container._hasClickListener = true;
  }
}

export function openArchiveDocument(docId: string): void {
  if (!state.archiveData) return;
  const doc = state.archiveData.find((d: any) => d.id === docId);
  if (!doc) return;

  state.activeArchiveDocId = docId;

  const badgeEl = document.getElementById("arch-view-badge");
  const titleEl = document.getElementById("arch-view-title");
  const metaEl = document.getElementById("arch-view-meta");
  const downloadBtn = document.getElementById("arch-view-download-btn") as HTMLAnchorElement | null;
  const toolsEl = document.getElementById("arch-lightbox-tools");
  const bodyEl = document.getElementById("archive-modal-body");
  const counterEl = document.getElementById("arch-modal-counter");

  if (badgeEl) badgeEl.textContent = (doc.extension || "").replace(".", "").toUpperCase();
  if (titleEl) titleEl.textContent = doc.title;
  if (metaEl) {
    const dateFormatted = doc.date ? doc.date.split("-").reverse().join(".") : doc.year;
    metaEl.textContent = `${doc.institution}  •  ${dateFormatted}  •  ${doc.size_formatted}  •  ${doc.doc_type}`;
  }
  if (downloadBtn) {
    downloadBtn.href = doc.file_url;
    downloadBtn.download = doc.original_filename;
  }

  const deleteBtn = document.getElementById("arch-view-delete-btn") as HTMLButtonElement | null;
  if (deleteBtn) {
    if (doc.id && String(doc.id).startsWith("arch-d1-") && state.role === "admin") {
      deleteBtn.classList.remove("hidden");
      deleteBtn.onclick = async () => {
        if (!confirm(`"${doc.title}" başlıklı arşiv belgesini silmek istediğinize emin misiniz?`)) return;
        deleteBtn.disabled = true;
        try {
          const res = await ctx.apiRequest("archive-docs", "DELETE", { docId: doc.id });
          if (res && res.ok) {
            closeArchiveViewer();
            ctx.showSyncToast("🗑️ Arşiv belgesi silindi");
            await loadArchiveManifest(true);
            renderArchiveView();
          } else {
            alert(res?.error || "Belge silinemedi");
          }
        } catch (err: any) {
          alert("Silme hatası: " + err.message);
        } finally {
          deleteBtn.disabled = false;
        }
      };
    } else {
      deleteBtn.classList.add("hidden");
      deleteBtn.onclick = null;
    }
  }

  const idx = state.archiveData.findIndex((d: any) => d.id === docId);
  if (counterEl) {
    counterEl.textContent = `${idx + 1} / ${state.archiveData.length}`;
  }

  // Reset Lightbox State
  state.lightboxState = { zoom: 1, rotate: 0, panX: 0, panY: 0, isDragging: false, startX: 0, startY: 0 };

  // Render Body by Format
  if (doc.format === "image" && bodyEl) {
    if (toolsEl) toolsEl.classList.remove("hidden");
    bodyEl.innerHTML = `
      <div class="lightbox-image-wrap" id="arch-lightbox-wrap">
        <img id="arch-lightbox-img" src="${doc.file_url}" class="lightbox-image" alt="${escapeHtml(doc.title)}" draggable="false" />
      </div>
    `;
    initLightboxPanZoom();
  } else if (doc.format === "pdf" && bodyEl) {
    if (toolsEl) toolsEl.classList.add("hidden");
    let pdfUrl = doc.file_url;
    if (pdfUrl.startsWith("data:application/pdf;base64,")) {
      try {
        const byteCharacters = atob(pdfUrl.split(",")[1]);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: "application/pdf" });
        pdfUrl = URL.createObjectURL(blob);
      } catch (e) {
        // fallback
      }
    }
    bodyEl.innerHTML = `
      <iframe src="${pdfUrl}#view=FitH" class="archive-pdf-iframe" title="${escapeHtml(doc.title)}"></iframe>
    `;
  } else if (bodyEl) {
    // doc / docx / text
    if (toolsEl) toolsEl.classList.add("hidden");
    if (doc.text_content) {
      bodyEl.innerHTML = `
        <div class="doc-text-reader">
          <div style="font-weight:700; font-size:1.3rem; margin-bottom:1rem; color:var(--text-primary);">${escapeHtml(doc.title)}</div>
          <div style="font-size:0.85rem; color:var(--text-muted); margin-bottom:1.5rem;">${escapeHtml(doc.institution)}  •  ${doc.date || doc.year}</div>
          <div style="white-space:pre-wrap; line-height:1.8;">${escapeHtml(doc.text_content)}</div>
        </div>
      `;
    } else {
      bodyEl.innerHTML = `
        <div class="doc-text-reader" style="text-align:center;">
          <div style="font-size:3rem; margin-bottom:1rem;">📝</div>
          <div style="font-size:1.2rem; font-weight:700; margin-bottom:0.5rem;">${escapeHtml(doc.title)}</div>
          <p style="color:var(--text-secondary); margin-bottom:1.5rem;">${escapeHtml(doc.description)}</p>
          <a href="${doc.file_url}" download="${doc.original_filename}" class="primary-btn" style="display:inline-block; padding:0.6rem 1.2rem;">⬇ Orijinal Word Belgesini İndir (${doc.size_formatted})</a>
        </div>
      `;
    }
  }

  document.getElementById("archive-viewer-backdrop")?.classList.remove("hidden");
}

export function closeArchiveViewer(): void {
  const backdrop = document.getElementById("archive-viewer-backdrop");
  if (backdrop) backdrop.classList.add("hidden");
  const bodyEl = document.getElementById("archive-modal-body");
  if (bodyEl) bodyEl.innerHTML = "";
  state.activeArchiveDocId = null;
}

export function prevArchiveDocument(): void {
  if (!state.archiveData || !state.activeArchiveDocId) return;
  const idx = state.archiveData.findIndex((d: any) => d.id === state.activeArchiveDocId);
  if (idx > 0) {
    openArchiveDocument(state.archiveData[idx - 1].id);
  } else {
    openArchiveDocument(state.archiveData[state.archiveData.length - 1].id);
  }
}

export function nextArchiveDocument(): void {
  if (!state.archiveData || !state.activeArchiveDocId) return;
  const idx = state.archiveData.findIndex((d: any) => d.id === state.activeArchiveDocId);
  if (idx < state.archiveData.length - 1) {
    openArchiveDocument(state.archiveData[idx + 1].id);
  } else {
    openArchiveDocument(state.archiveData[0].id);
  }
}

export function initLightboxPanZoom(): void {
  const wrap = document.getElementById("arch-lightbox-wrap");
  const img = document.getElementById("arch-lightbox-img");
  if (!wrap || !img) return;

  const updateTransform = () => {
    img.style.transform = `translate(${state.lightboxState.panX}px, ${state.lightboxState.panY}px) scale(${state.lightboxState.zoom}) rotate(${state.lightboxState.rotate}deg)`;
  };

  wrap.addEventListener("wheel", (e: WheelEvent) => {
    e.preventDefault();
    const delta = e.deltaY < 0 ? 0.2 : -0.2;
    state.lightboxState.zoom = Math.max(0.5, Math.min(6, state.lightboxState.zoom + delta));
    const btn = document.getElementById("arch-zoom-reset");
    if (btn) btn.textContent = `${Math.round(state.lightboxState.zoom * 100)}%`;
    updateTransform();
  }, { passive: false });

  wrap.addEventListener("mousedown", (e: MouseEvent) => {
    state.lightboxState.isDragging = true;
    state.lightboxState.startX = e.clientX - state.lightboxState.panX;
    state.lightboxState.startY = e.clientY - state.lightboxState.panY;
  });

  window.addEventListener("mousemove", (e: MouseEvent) => {
    if (!state.lightboxState.isDragging) return;
    state.lightboxState.panX = e.clientX - state.lightboxState.startX;
    state.lightboxState.panY = e.clientY - state.lightboxState.startY;
    updateTransform();
  });

  window.addEventListener("mouseup", () => {
    state.lightboxState.isDragging = false;
  });

  let lastTouchDist = 0;
  wrap.addEventListener("touchstart", (e: TouchEvent) => {
    if (e.touches.length === 1) {
      state.lightboxState.isDragging = true;
      state.lightboxState.startX = e.touches[0].clientX - state.lightboxState.panX;
      state.lightboxState.startY = e.touches[0].clientY - state.lightboxState.panY;
    } else if (e.touches.length === 2) {
      lastTouchDist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
    }
  });

  wrap.addEventListener("touchmove", (e: TouchEvent) => {
    if (e.touches.length === 1 && state.lightboxState.isDragging) {
      state.lightboxState.panX = e.touches[0].clientX - state.lightboxState.startX;
      state.lightboxState.panY = e.touches[0].clientY - state.lightboxState.startY;
      updateTransform();
    } else if (e.touches.length === 2) {
      const dist = Math.hypot(
        e.touches[0].clientX - e.touches[1].clientX,
        e.touches[0].clientY - e.touches[1].clientY
      );
      if (lastTouchDist > 0) {
        const factor = dist / lastTouchDist;
        state.lightboxState.zoom = Math.max(0.5, Math.min(6, state.lightboxState.zoom * factor));
        const btn = document.getElementById("arch-zoom-reset");
        if (btn) btn.textContent = `${Math.round(state.lightboxState.zoom * 100)}%`;
        updateTransform();
      }
      lastTouchDist = dist;
    }
  });

  wrap.addEventListener("touchend", () => {
    state.lightboxState.isDragging = false;
    lastTouchDist = 0;
  });
}

export function adjustLightboxZoom(delta: number): void {
  state.lightboxState.zoom = Math.max(0.5, Math.min(6, state.lightboxState.zoom + delta));
  const btn = document.getElementById("arch-zoom-reset");
  if (btn) btn.textContent = `${Math.round(state.lightboxState.zoom * 100)}%`;
  const img = document.getElementById("arch-lightbox-img");
  if (img) {
    img.style.transform = `translate(${state.lightboxState.panX}px, ${state.lightboxState.panY}px) scale(${state.lightboxState.zoom}) rotate(${state.lightboxState.rotate}deg)`;
  }
}

export function resetLightboxZoom(): void {
  state.lightboxState.zoom = 1;
  state.lightboxState.panX = 0;
  state.lightboxState.panY = 0;
  const btn = document.getElementById("arch-zoom-reset");
  if (btn) btn.textContent = "100%";
  const img = document.getElementById("arch-lightbox-img");
  if (img) {
    img.style.transform = `translate(0px, 0px) scale(1) rotate(${state.lightboxState.rotate}deg)`;
  }
}

export function rotateLightboxImage(): void {
  state.lightboxState.rotate = (state.lightboxState.rotate + 90) % 360;
  const img = document.getElementById("arch-lightbox-img");
  if (img) {
    img.style.transform = `translate(${state.lightboxState.panX}px, ${state.lightboxState.panY}px) scale(${state.lightboxState.zoom}) rotate(${state.lightboxState.rotate}deg)`;
  }
}
